'use client';
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Dictionary, Notification } from '@witmetrics/utils';
import { UnauthorizedError } from '@witmetrics/utils/errors';
import { ACCOUNT_TYPES } from '@witmetrics/utils/constants';
import { isNewOrderNotification } from '@witmetrics/utils/notifications';
import { useActiveAccount, useDispatch } from '@witmetrics/data-store';
import {
  ACTIVE_LEAD_PARAMS,
  ACTIVE_ORDER_PARAMS,
  ACTIVE_PROJECT_PARAMS,
  addRecentId,
  DEFAULT_DIALOGS,
  DEFAULT_OVERLAYS,
  type DialogName,
  DIALOGS,
  isFileNotification,
  isLeadNotification,
  isProjectNotification,
  OVERLAYS,
} from '@witmetrics/unison-core';
import { mapToId } from '@witmetrics/utils/arrays';
import { showErrorToaster } from '@/utils/toasters';
import { usePath } from '@/hooks/usePath';
import { useSockets } from '@/providers/SocketsProvider';
import { getCrmUrl } from '@/api/urls';
import { useMountEffect } from '@/hooks/useMountEffect';
import { useId } from '@/hooks/useId';
import { useIds } from '@/hooks/useIds';
import { useDate } from '@/hooks/useDate';
import {
  fetchProjectLead,
  fetchRecents,
  getNotificationAccount,
  updateActiveAccount,
  updateReadNotification,
  useStoredAccountId,
  warnBeforeUnload,
} from './utils';

const { PRACTICE } = ACCOUNT_TYPES;

type AppStateValue = {
  activeLeadId: number | undefined;
  recentLeadIds: number[];
  activeProjectId: number | undefined;
  recentProjectIds: number[];
  activeOrderId: number | undefined;
  recentOrderIds: number[];
  activeServiceId: number | undefined;
  activeCalendarDate: string | null;
  activeDialogs: Dictionary<boolean>;
  activeOverlays: Dictionary<boolean>;
  setActiveDialogs: Dispatch<SetStateAction<{ [p: string]: boolean }>>;
  onLogin: () => void;
  onApiError: (err: any, message: string, cb?: Function) => void;
  onSelectLead: (leadId: number) => void;
  onClearActiveLead: () => void;
  onClearRecentLeads: () => void;
  onClearActiveProject: () => void;
  onClearRecentProjects: () => void;
  onSelectProject: (projectId: number, params?: Dictionary<any>) => void;
  onClearActiveOrder: () => void;
  onClearRecentOrders: () => void;
  onSelectOrder: (orderId: number, params?: Dictionary<any>) => void;
  onSelectService: (serviceId: number) => void;
  onClearActiveService: () => void;
  onStartTask: (date: string) => void;
  onOpenAgenda: (date: string) => void;
  onOpenNotification: (notification: Notification) => void;
  onToggleUnloadWarning: (activate: boolean) => void;
  onOpenDialog: (dialogName: DialogName) => void;
  onCloseDialog: (dialogName: DialogName) => void;
  onOpenOverlay: (overlayName: string) => void;
  onCloseOverlay: (overlayName: string) => void;
};

const AppStateContext = createContext<AppStateValue | undefined>(undefined);

export default function AppStateProvider({ children }: PropsWithChildren) {
  const activeProjectRef = useRef<number | undefined>();
  const activeOrderRef = useRef<number | undefined>();
  const activeServiceRef = useRef<number | undefined>();
  const loginCallbacks = useRef<Function[]>([]);
  const dispatch = useDispatch();
  const activeAccount = useActiveAccount();
  const { setApiErrorCallback } = useSockets();
  const { navigate, pushParams, removeParams } = usePath();
  const [activeLeadId, setActiveLeadId, { clear: clearActiveLeadId }] = useId();
  const [activeProjectId, setActiveProjectId, { clear: clearActiveProjectId }] =
    useId();
  const [activeOrderId, setActiveOrderId, { clear: clearActiveOrderId }] =
    useId();
  const [activeServiceId, setActiveServiceId, { clear: clearActiveServiceId }] =
    useId();
  const [recentLeadIds, setRecentLeadIds, { clear: clearRecentLeadIds }] =
    useIds();
  const [
    recentProjectIds,
    setRecentProjectIds,
    { clear: clearRecentProjectIds },
  ] = useIds();
  const [recentOrderIds, setRecentOrderIds, { clear: clearRecentOrderIds }] =
    useIds();
  const [activeCalendarDate, setActiveCalendarDate] = useDate();
  const [activeDialogs, setActiveDialogs] = useState(DEFAULT_DIALOGS);
  const [activeOverlays, setActiveOverlays] = useState(DEFAULT_OVERLAYS);
  const [storedAccountId, setStoredAccountId] = useStoredAccountId();

  useMountEffect(() => {
    setApiErrorCallback(handleSocketError);
  });

  useEffect(() => {
    // TODO: This logic needs to differentiate between labs & practices
    if (activeAccount === null) {
      // activeAccount has been fetched & isn't set
      handleOpenDialog(DIALOGS.ACCOUNTS);
    } else if (activeAccount === undefined) {
      // still waiting for activeAccount to be fetched
      return;
    } else if (activeAccount.accountId !== storedAccountId) {
      // activeAccount has changed
      handleResetAccountItems();
      handleFetchRecents();
    } else if (activeAccount.accountId) {
      // Account hasn't changed, just needs an initial lead fetch
      handleFetchRecents();
    }
  }, [activeAccount]);

  const handleResetAccountItems = () => {
    setActiveProjectId(activeProjectRef.current);
    setActiveOrderId(activeOrderRef.current);
    setActiveServiceId(activeServiceRef.current);
    activeProjectRef.current = undefined;
    activeOrderRef.current = undefined;
    activeServiceRef.current = undefined;
    clearRecentLeadIds();
    clearRecentProjectIds();
    clearRecentOrderIds();
    setStoredAccountId(activeAccount!.accountId);
  };

  const handleFetchRecents = async () => {
    if (activeAccount?.accountType !== PRACTICE) {
      return console.warn(
        'handleFetchRecents: Recents are only available for practice accounts'
      );
    }
    const { leadIds, projects } = await fetchRecents();
    dispatch.addProjects(projects);
    setRecentLeadIds(leadIds);
    setRecentProjectIds(mapToId(projects));
  };

  const handleOpenDialog = (dialogName: DialogName) => {
    setActiveDialogs((dialogs) => ({ ...dialogs, [dialogName]: true }));
  };

  const handleCloseDialog = (dialogName: DialogName) => {
    setActiveDialogs((dialogs) => ({ ...dialogs, [dialogName]: false }));
  };

  const handleOpenOverlay = (overlayName: string) => {
    setActiveOverlays((overlays) => ({ ...overlays, [overlayName]: true }));
  };

  const handleCloseOverlay = (overlayName: string) => {
    setActiveOverlays((overlays) => ({ ...overlays, [overlayName]: false }));
    if (overlayName === OVERLAYS.AGENDA) {
      setActiveCalendarDate(null);
    }
  };

  const handleSocketError = () => {
    handleApiError(new UnauthorizedError('', ''), '');
  };

  const handleApiError = (err: any, toasterMessage: string, cb?: Function) => {
    if (err instanceof UnauthorizedError) {
      if (cb) loginCallbacks.current.push(cb);
      if (!activeDialogs[DIALOGS.LOGIN]) handleOpenDialog(DIALOGS.LOGIN);
    } else {
      showErrorToaster(toasterMessage);
      throw err;
    }
  };

  const handleSetActiveProject = async (
    projectId: number,
    params: Dictionary<any> = {}
  ) => {
    if (projectId === activeProjectId) return;
    // Optionally include extra params for selecting a tab, file, etc.
    pushParams({ ...params, activeProjectId: projectId });
    setActiveProjectId(projectId);
    setRecentProjectIds((ids) => addRecentId(ids, projectId));
    // Async add project's lead to top of recentLeadIds
    const projectLead = await fetchProjectLead(projectId);
    if (projectLead) {
      dispatch.addLeads([projectLead]);
      setRecentLeadIds((ids) => addRecentId(ids, projectLead.id));
    }
  };

  const handleSetActiveOrder = (
    orderId: number,
    params: Dictionary<any> = {}
  ) => {
    if (orderId === activeOrderId) return;
    // Optionally include extra params for selecting a tab, file, etc.
    pushParams({ ...params, activeOrderId: orderId });
    setActiveOrderId(orderId);
    setRecentOrderIds((ids) => addRecentId(ids, orderId));
  };

  const handleSetActiveLead = (leadId: number) => {
    if (leadId === activeLeadId) return;
    pushParams({ activeLeadId: leadId });
    setActiveLeadId(leadId);
    setRecentLeadIds((ids) => addRecentId(ids, leadId));
  };

  const handleClearActiveProject = () => {
    clearActiveProjectId();
    removeParams(ACTIVE_PROJECT_PARAMS);
  };

  const handleClearActiveOrder = () => {
    clearActiveOrderId();
    removeParams(ACTIVE_ORDER_PARAMS);
  };

  const handleClearActiveLead = () => {
    clearActiveLeadId();
    removeParams(ACTIVE_LEAD_PARAMS);
  };

  const handleSetActiveService = (serviceId: number) => {
    if (serviceId !== activeServiceId) setActiveServiceId(serviceId);
  };

  const handleStartTask = (date: string) => {
    setActiveCalendarDate(date);
    handleOpenDialog(DIALOGS.TASK);
  };

  const handleOpenAgenda = (date: string) => {
    setActiveCalendarDate(date);
    handleOpenOverlay(OVERLAYS.AGENDA);
  };

  const handleLogin = () => {
    if (loginCallbacks.current) {
      loginCallbacks.current.forEach((cb) => cb());
    }
    handleCloseDialog(DIALOGS.LOGIN);
    loginCallbacks.current = [];
  };

  const handleToggleUnloadWarning = (activate: boolean) => {
    window.removeEventListener('beforeunload', warnBeforeUnload);
    if (activate) window.addEventListener('beforeunload', warnBeforeUnload);
  };

  const handleOpenNotification = async (notification: Notification) => {
    // Make sure the correct account is active
    const { accountType, accountId } = getNotificationAccount(notification);
    if (
      activeAccount?.accountType !== accountType ||
      activeAccount?.accountId !== accountId
    ) {
      const account = await updateActiveAccount(accountType, accountId);
      if (isProjectNotification(notification)) {
        // Prevent activeProjectId from being cleared by handleResetAccountItems
        activeProjectRef.current = notification.content.unisonProjectId;
      } else if (isNewOrderNotification(notification)) {
        activeOrderRef.current = notification.content.orderId;
      }
      dispatch.addActiveAccount(account);
    }

    // No need to wait on this, the socket event will handle it
    if (!notification.isRead) updateReadNotification(notification.id);

    // Determine what to open / navigate to
    if (isProjectNotification(notification)) {
      let params: Dictionary<number> = {};
      if ('noteId' in notification.content) {
        params.highlightedNoteId = notification.content.noteId;
      }
      if ('taskId' in notification.content) {
        params.highlightedTaskId = notification.content.taskId;
      }
      // if ('fileId' in notification.content) params.highlightedFileId = notification.content.fileId;
      return handleSetActiveProject(
        notification.content.unisonProjectId,
        params
      );
    } else if (isFileNotification(notification)) {
      if ('unisonProjectId' in notification.content) {
        return handleSetActiveProject(notification.content.unisonProjectId!, {
          activeFileId: notification.content.fileId,
          highlightedNoteId: notification.content.noteId,
        });
      } else {
        // TODO: Open file directly
        showErrorToaster('File notifications are not yet supported');
      }
    } else if (isNewOrderNotification(notification)) {
      return handleSetActiveOrder(notification.content.orderId);
    } else if (isLeadNotification(notification)) {
      navigate(getCrmUrl(), { activeLeadId: notification.content.leadId });
    } else {
      showErrorToaster('Error opening notification');
      throw Error(
        // @ts-ignore
        `Unrecognized notificationTypeId: ${notification.notificationTypeId}`
      );
    }
  };

  const contextValue = {
    activeLeadId,
    recentLeadIds,
    activeProjectId,
    recentProjectIds,
    activeOrderId,
    recentOrderIds,
    activeServiceId,
    activeCalendarDate,
    activeDialogs,
    activeOverlays,
    setActiveDialogs,
    onLogin: handleLogin,
    onApiError: handleApiError,
    onClearActiveProject: handleClearActiveProject,
    onClearActiveOrder: handleClearActiveOrder,
    onClearActiveService: clearActiveServiceId,
    onClearActiveLead: handleClearActiveLead,
    onClearRecentLeads: clearRecentLeadIds,
    onClearRecentProjects: clearRecentProjectIds,
    onClearRecentOrders: clearRecentOrderIds,
    onSelectProject: handleSetActiveProject,
    onSelectOrder: handleSetActiveOrder,
    onSelectService: handleSetActiveService,
    onSelectLead: handleSetActiveLead,
    onStartTask: handleStartTask,
    onOpenAgenda: handleOpenAgenda,
    onOpenNotification: handleOpenNotification,
    onToggleUnloadWarning: handleToggleUnloadWarning,
    onOpenDialog: handleOpenDialog,
    onCloseDialog: handleCloseDialog,
    onOpenOverlay: handleOpenOverlay,
    onCloseOverlay: handleCloseOverlay,
  };

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw Error('useAppState must be used within the AppStateProvider');
  }
  return context;
}
